import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Play, Mail, Lock, User, AlertCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VODFooter from '../components/VODFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Google Icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const LiveCustomerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check URL for default tab (signup from popup redirect)
  const defaultTab = searchParams.get('tab') === 'signup' ? 'register' : 'login';
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Always show Google button - Google OAuth is configured for this site
  const [error, setError] = useState('');
  const googleEnabled = true;
  const [promoWallpaper, setPromoWallpaper] = useState(null);
  const [eventBranding, setEventBranding] = useState(null); // Custom branding from event
  const isMobile = useIsMobile();
  
  // Forgot password state
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false); // 'email' | 'code' | false
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState('');
  
  // Check if user is coming from LTVVOD.com domain or /vod path
  const isVODDomain = searchParams.get('from') === 'ltvvod' || 
    searchParams.get('from') === 'vod' ||
    window.location.hostname.includes('ltvvod') ||
    document.referrer.includes('/vod') ||
    sessionStorage.getItem('loginSource') === 'vod' ||
    sessionStorage.getItem('loginSource') === 'ltvvod';
  
  // Store VOD source in session for persistence (preserve actual source)
  useEffect(() => {
    const fromParam = searchParams.get('from');
    if (fromParam === 'ltvvod') {
      sessionStorage.setItem('loginSource', 'ltvvod');
    } else if (fromParam === 'vod') {
      sessionStorage.setItem('loginSource', 'vod');
    } else if (window.location.hostname.includes('ltvvod')) {
      sessionStorage.setItem('loginSource', 'ltvvod');
    }
  }, [searchParams]);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', full_name: '' });

  useEffect(() => {
    // Check for error in URL params
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(getErrorMessage(errorParam));
    }

    // First try to fetch event-specific branding if coming from an event
    fetchEventBranding();
  }, [searchParams]);
  
  // Fetch event-specific branding (custom logo and wallpaper) if user is coming from an event
  const fetchEventBranding = async () => {
    // Check redirect param to get event slug
    // IMPORTANT: Event branding should ONLY show when user is logging in from a specific event page
    // NOT from the main homepage - even if intendedEvent exists in localStorage
    const redirectParam = searchParams.get('redirect');
    
    let eventSlug = null;
    
    // Parse event slug ONLY from redirect URL (e.g., /live/event/my-event)
    // This ensures the user actually came from an event page to see event branding
    if (redirectParam) {
      const match = redirectParam.match(/\/live\/event\/([^?/]+)/);
      if (match) {
        eventSlug = match[1];
      }
    }
    
    // REMOVED: Do NOT use intendedEvent from localStorage for branding
    // intendedEvent may persist from previous sessions and cause incorrect branding
    // on the main homepage login page. It's only used for post-login redirects.
    
    if (eventSlug) {
      try {
        const response = await axios.get(`${API}/livestream/events/${eventSlug}`);
        const event = response.data;
        
        // Check if event has custom branding
        if (event.custom_logo_url || event.custom_signin_wallpaper_url || event.custom_signin_wallpaper_mobile_url) {
          setEventBranding({
            custom_logo_url: event.custom_logo_url,
            custom_signin_wallpaper_url: event.custom_signin_wallpaper_url,
            custom_signin_wallpaper_mobile_url: event.custom_signin_wallpaper_mobile_url,
            title: event.title,
            restricted_navigation: event.restricted_navigation
          });
          // If event has custom wallpaper, don't fetch the general promo wallpaper
          if (event.custom_signin_wallpaper_url || event.custom_signin_wallpaper_mobile_url) {
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching event branding:', err);
      }
    }
    
    // Fetch general promotional wallpaper if no event-specific wallpaper
    fetchPromoWallpaper();
  };

  const fetchPromoWallpaper = async () => {
    try {
      const response = await axios.get(`${API}/login-settings/wallpaper`);
      if (response.data.wallpaper) {
        setPromoWallpaper(response.data.wallpaper);
      }
    } catch (err) {
      console.error('Error fetching promo wallpaper:', err);
    }
  };

  const getErrorMessage = (errorCode) => {
    const errors = {
      'auth_failed': 'Authentication failed. Please try again.',
      'no_code': 'No authorization code received.',
      'token_exchange_failed': 'Failed to authenticate with Google.',
      'redirect_uri_mismatch': 'Google sign-in configuration error. Please contact support.',
      'invalid_client': 'Google sign-in configuration error. Please contact support.',
      'invalid_grant': 'Google sign-in expired. Please try again.',
      'userinfo_failed': 'Failed to get user info from Google.',
      'no_email': 'No email found in your Google account.',
      'no_id_token': 'Failed to authenticate with Google. Please try again.',
      'token_decode_failed': 'Failed to authenticate with Google. Please try again.',
      'invalid_id_token': 'Failed to authenticate with Google. Please try again.',
      'timeout': 'Google sign-in timed out. Please try again.',
      'complete_failed': 'Login failed. Please try again.',
      'oauth_failed': 'Google sign-in failed. Please try again.'
    };
    return errors[errorCode] || 'An error occurred. Please try again.';
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // Pass redirect parameter and from domain to Google OAuth flow via backend
    const redirectParam = searchParams.get('redirect');
    const fromDomain = searchParams.get('from');
    
    let oauthUrl = `${window.location.origin}/api/auth/google/login`;
    const params = [];
    
    if (redirectParam) {
      params.push(`redirect_to=${encodeURIComponent(redirectParam)}`);
    }
    if (fromDomain) {
      params.push(`from=${encodeURIComponent(fromDomain)}`);
    }
    
    if (params.length > 0) {
      oauthUrl += '?' + params.join('&');
    }
    
    // Use target="_top" to break out of any iframe.
    // ltvvod.com uses iframe/mask forwarding and Google blocks OAuth inside iframes.
    // When not in an iframe, _top is the current window so this works in all cases.
    const a = document.createElement('a');
    a.href = oauthUrl;
    a.target = '_top';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/livestream/auth/login`, loginData);
      
      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem('livestreamToken', token);
        localStorage.setItem('livestreamCustomer', JSON.stringify(response.data.customer));
        
        // If staff member, store admin token and staff info for dashboard access
        if (response.data.is_staff && response.data.admin_token) {
          localStorage.setItem('adminToken', response.data.admin_token);
          localStorage.setItem('staffPermissions', JSON.stringify(response.data.staff_permissions || {}));
          localStorage.setItem('isStaff', 'true');
        }
        
        // Store restricted event data if coming from a restricted event
        if (eventBranding?.restricted_navigation) {
          localStorage.setItem('restrictedEvent', JSON.stringify(eventBranding));
        }
        
        // PRIORITY 0: Check if user came from ltvvod.com or vod section
        const fromDomain = searchParams.get('from');
        const redirectParam = searchParams.get('redirect');
        const creatorPageReturn = sessionStorage.getItem('creatorPageReturn');
        
        // Handle ltvvod.com domain redirects - ALWAYS redirect to VOD section
        if (fromDomain === 'ltvvod') {
          let redirectPath = redirectParam || creatorPageReturn || '/vod';
          sessionStorage.removeItem('creatorPageReturn');
          sessionStorage.removeItem('loginSource');
          
          // On ltvvod.com, the VOD homepage is at root '/' not '/vod'
          if (window.location.hostname.includes('ltvvod') && (redirectPath === '/vod' || redirectPath === '/vod/')) {
            redirectPath = '/';
          }
          
          const vodDomain = window.location.origin;
          console.log('[Login] Redirecting to:', vodDomain + redirectPath);
          window.location.href = `${vodDomain}${redirectPath}`;
          return;
        }
        
        // Handle 'from=vod' for same-domain VOD navigation
        if (fromDomain === 'vod') {
          // Always redirect to VOD section - use redirect param if provided, otherwise /vod
          const redirectPath = redirectParam || creatorPageReturn || '/vod';
          sessionStorage.removeItem('creatorPageReturn');
          sessionStorage.removeItem('loginSource');
          console.log('[Login] Redirecting to VOD path:', redirectPath);
          navigate(redirectPath);
          return;
        }
        
        // Check if user came from VOD via session storage (fallback)
        const loginSource = sessionStorage.getItem('loginSource');
        if (loginSource === 'ltvvod') {
          let redirectPath = redirectParam || '/vod';
          if (window.location.hostname.includes('ltvvod') && (redirectPath === '/vod' || redirectPath === '/vod/')) {
            redirectPath = '/';
          }
          sessionStorage.removeItem('loginSource');
          const vodDomain = window.location.origin;
          console.log('[Login] Redirecting to (from session):', vodDomain + redirectPath);
          window.location.href = `${vodDomain}${redirectPath}`;
          return;
        }
        if (loginSource === 'vod') {
          const redirectPath = redirectParam || '/vod';
          sessionStorage.removeItem('loginSource');
          console.log('[Login] Redirecting to VOD (from session):', redirectPath);
          navigate(redirectPath);
          return;
        }
        
        // PRIORITY 1: Check for intended event OR event from redirect param
        // This handles both direct intendedEvent and share link redirects
        let eventSlug = localStorage.getItem('intendedEvent');
        
        // If no intendedEvent, try to extract from redirect param
        if (!eventSlug && redirectParam) {
          const match = redirectParam.match(/\/live\/event\/([^?/]+)/);
          if (match) {
            eventSlug = match[1];
          }
        }
        
        if (eventSlug) {
          localStorage.removeItem('intendedEvent');
          
          // Check if user already has a ticket for this event
          try {
            const ticketsResponse = await axios.get(`${API}/livestream/my-tickets`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const tickets = ticketsResponse.data.tickets || [];
            const hasTicketForEvent = tickets.some(t => 
              t.event?.slug === eventSlug || 
              t.event_slug === eventSlug || 
              t.event_id === eventSlug
            );
            
            if (hasTicketForEvent) {
              // User has ticket - go to My Tickets
              navigate('/live/my-tickets');
            } else {
              // User doesn't have ticket - go back to event page to buy
              navigate(`/live/event/${eventSlug}`);
            }
          } catch (err) {
            // If ticket check fails, go to event page
            console.error('Error checking tickets:', err);
            navigate(`/live/event/${eventSlug}`);
          }
          return;
        }
        
        // PRIORITY 2: Check for redirect parameter in URL (non-event redirects)
        if (redirectParam && !redirectParam.includes('/live/my-tickets')) {
          sessionStorage.removeItem('homepageLoginRedirect');
          navigate(redirectParam);
          return;
        }
        
        // PRIORITY 3: Check sessionStorage for homepage redirect
        const homepageRedirect = sessionStorage.getItem('homepageLoginRedirect');
        if (homepageRedirect) {
          sessionStorage.removeItem('homepageLoginRedirect');
          navigate(homepageRedirect);
          return;
        }
        
        // Default: Go to My Tickets
        navigate('/live/my-tickets');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/livestream/auth/register`, registerData);
      
      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem('livestreamToken', token);
        localStorage.setItem('livestreamCustomer', JSON.stringify(response.data.customer));
        
        // Store restricted event data if coming from a restricted event
        if (eventBranding?.restricted_navigation) {
          localStorage.setItem('restrictedEvent', JSON.stringify(eventBranding));
        }
        
        // PRIORITY 0: Check if user came from ltvvod.com or vod section
        const fromDomain = searchParams.get('from');
        const redirectParam = searchParams.get('redirect');
        const creatorPageReturn = sessionStorage.getItem('creatorPageReturn');
        
        // Handle ltvvod.com domain redirects - ALWAYS redirect to VOD section
        if (fromDomain === 'ltvvod') {
          let redirectPath = redirectParam || creatorPageReturn || '/vod';
          sessionStorage.removeItem('creatorPageReturn');
          sessionStorage.removeItem('loginSource');
          
          // On ltvvod.com, the VOD homepage is at root '/' not '/vod'
          if (window.location.hostname.includes('ltvvod') && (redirectPath === '/vod' || redirectPath === '/vod/')) {
            redirectPath = '/';
          }
          
          const vodDomain = window.location.origin;
          console.log('[Register] Redirecting to VOD:', vodDomain + redirectPath);
          window.location.href = `${vodDomain}${redirectPath}`;
          return;
        }
        
        // Handle 'from=vod' for same-domain VOD navigation
        if (fromDomain === 'vod') {
          // Always redirect to VOD section - use redirect param if provided, otherwise /vod
          const redirectPath = redirectParam || creatorPageReturn || '/vod';
          sessionStorage.removeItem('creatorPageReturn');
          sessionStorage.removeItem('loginSource');
          console.log('[Register] Redirecting to VOD path:', redirectPath);
          navigate(redirectPath);
          return;
        }
        
        // Check if user came from VOD via session storage (fallback)
        const loginSource = sessionStorage.getItem('loginSource');
        if (loginSource === 'ltvvod') {
          let redirectPath = redirectParam || '/vod';
          if (window.location.hostname.includes('ltvvod') && (redirectPath === '/vod' || redirectPath === '/vod/')) {
            redirectPath = '/';
          }
          sessionStorage.removeItem('loginSource');
          const vodDomain = window.location.origin;
          console.log('[Register] Redirecting to VOD (from session):', vodDomain + redirectPath);
          window.location.href = `${vodDomain}${redirectPath}`;
          return;
        }
        if (loginSource === 'vod') {
          const redirectPath = redirectParam || '/vod';
          sessionStorage.removeItem('loginSource');
          console.log('[Register] Redirecting to VOD (from session):', redirectPath);
          navigate(redirectPath);
          return;
        }
        
        // PRIORITY 1: Check for intended event OR event from redirect param
        // New users always go to event page to buy ticket
        let eventSlug = localStorage.getItem('intendedEvent');
        
        // If no intendedEvent, try to extract from redirect param
        if (!eventSlug && redirectParam) {
          const match = redirectParam.match(/\/live\/event\/([^?/]+)/);
          if (match) {
            eventSlug = match[1];
          }
        }
        
        if (eventSlug) {
          localStorage.removeItem('intendedEvent');
          // New registration = no tickets yet, go to event page to buy
          navigate(`/live/event/${eventSlug}`);
          return;
        }
        
        // PRIORITY 2: Check for redirect parameter in URL (non-event redirects)
        if (redirectParam && !redirectParam.includes('/live/my-tickets')) {
          sessionStorage.removeItem('homepageLoginRedirect');
          navigate(redirectParam);
          return;
        }
        
        // PRIORITY 3: Check sessionStorage for homepage redirect
        const homepageRedirect = sessionStorage.getItem('homepageLoginRedirect');
        if (homepageRedirect) {
          sessionStorage.removeItem('homepageLoginRedirect');
          navigate(homepageRedirect);
          return;
        }
        
        // Default: Go to My Tickets
        navigate('/live/my-tickets');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password handlers
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');
    setForgotSuccess('');

    try {
      const response = await axios.post(`${API}/livestream/auth/forgot-password`, { email: forgotEmail });
      if (response.data.success) {
        setGeneratedCode(response.data.code);
        setForgotPasswordMode('code');
        setForgotSuccess('Your reset code is shown below. Use it to set a new password.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate reset code.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');
    setForgotSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setForgotLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      setForgotLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/livestream/auth/reset-password`, {
        email: forgotEmail,
        code: resetCode,
        new_password: newPassword
      });
      if (response.data.success) {
        setForgotSuccess('Password reset successfully! You can now sign in.');
        setForgotPasswordMode(false);
        setGeneratedCode('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
        setForgotEmail('');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setForgotLoading(false);
    }
  };

  const exitForgotPassword = () => {
    setForgotPasswordMode(false);
    setGeneratedCode('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotEmail('');
    setError('');
    setForgotSuccess('');
  };

  // Forgot Password UI component
  const renderForgotPasswordUI = () => {
    if (forgotPasswordMode === 'code') {
      return (
        <div className="space-y-4" data-testid="forgot-password-code-form">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-white">Reset Your Password</h3>
            <p className="text-sm text-gray-400 mt-1">Enter the code below with your new password</p>
          </div>

          {generatedCode && (
            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 text-center">
              <p className="text-sm text-green-300 mb-1">Your Reset Code:</p>
              <p className="text-2xl font-mono font-bold text-green-400 tracking-wider" data-testid="reset-code-display">{generatedCode}</p>
              <p className="text-xs text-green-300/70 mt-1">Expires in 15 minutes</p>
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label className="text-gray-300">Reset Code</Label>
              <Input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                className="bg-[#1a1a1a] border-[#333333] text-white mt-1"
                placeholder="Enter 6-digit code"
                required
                data-testid="reset-code-input"
              />
            </div>
            <div>
              <Label className="text-gray-300">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                  placeholder="New password (min 6 chars)"
                  required
                  data-testid="new-password-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              disabled={forgotLoading}
              data-testid="reset-password-submit"
            >
              {forgotLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting...</> : 'Reset Password'}
            </Button>
          </form>
          <button
            type="button"
            onClick={exitForgotPassword}
            className="w-full text-sm text-gray-400 hover:text-white transition-colors"
            data-testid="back-to-login"
          >
            Back to Sign In
          </button>
        </div>
      );
    }

    // Email entry step
    return (
      <div className="space-y-4" data-testid="forgot-password-email-form">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-white">Forgot Password?</h3>
          <p className="text-sm text-gray-400 mt-1">Enter your email to get a reset code</p>
        </div>
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div>
            <Label className="text-gray-300">Email Address</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                placeholder="your@email.com"
                required
                data-testid="forgot-email-input"
              />
            </div>
          </div>
          <Button 
            type="submit" 
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            disabled={forgotLoading}
            data-testid="forgot-password-submit"
          >
            {forgotLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : 'Get Reset Code'}
          </Button>
        </form>
        <button
          type="button"
          onClick={exitForgotPassword}
          className="w-full text-sm text-gray-400 hover:text-white transition-colors"
          data-testid="back-to-login"
        >
          Back to Sign In
        </button>
      </div>
    );
  };

  // If there's event-specific branding with custom wallpaper, use that first
  // Use mobile wallpaper on mobile devices if available
  const eventWallpaperUrl = isMobile && eventBranding?.custom_signin_wallpaper_mobile_url 
    ? eventBranding.custom_signin_wallpaper_mobile_url 
    : eventBranding?.custom_signin_wallpaper_url;
    
  if (eventBranding && eventWallpaperUrl) {
    return (
      <div className="min-h-screen relative">
        {/* Full Background Image from Event Branding */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${eventWallpaperUrl})` }}
        />
        {/* Dark overlay to ensure login box is readable */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Content Container - Centered */}
        <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Custom Logo or Default Logo */}
            <div className="text-center mb-8">
              {eventBranding.custom_logo_url ? (
                <div className="inline-flex items-center justify-center">
                  <img 
                    src={eventBranding.custom_logo_url} 
                    alt={eventBranding.title || 'Event'} 
                    className="max-h-16 max-w-[200px] object-contain"
                  />
                </div>
              ) : (
                <div 
                  className={`inline-flex items-center gap-3 ${eventBranding.restricted_navigation ? 'cursor-default' : 'cursor-pointer'}`}
                  onClick={() => !eventBranding.restricted_navigation && navigate(isVODDomain ? '/vod' : '/live')}
                >
                  <img 
                    src={isVODDomain ? "/ltv-vod-logo.png" : "/ltv-live-logo.png"}
                    alt={isVODDomain ? "LTV VOD" : "LTV Live"}
                    className={isVODDomain ? "h-16 sm:h-20 object-contain" : "w-12 h-12 object-contain brightness-0 invert"}
                  />
                  {!isVODDomain && <span className="text-2xl font-bold text-white drop-shadow-lg">LTV Live</span>}
                </div>
              )}
            </div>

            <Card className="bg-[#111111]/95 border-[#222222] backdrop-blur-sm shadow-2xl">
              <CardHeader className="text-center">
                <CardTitle className="text-white text-2xl">Welcome</CardTitle>
                <CardDescription className="text-gray-400">
                  {eventBranding.title ? `Sign in to access ${eventBranding.title}` : (isVODDomain ? 'Sign in to access LTV VOD' : 'Sign in to access your live stream tickets')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && error === 'STAFF_PORTAL_REDIRECT' ? (
                  <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4 mb-4 text-amber-300">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm font-semibold">Staff Account Detected</span>
                    </div>
                    <p className="text-sm mb-3">This email is registered as a staff/admin account. Staff members must log in through the Admin Portal.</p>
                    <a href="/admin" className="inline-block bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                      Go to Admin Portal
                    </a>
                  </div>
                ) : error ? (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                ) : null}

                {forgotSuccess && (
                  <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-3 mb-4 flex items-center gap-2 text-green-400">
                    <span className="text-sm">{forgotSuccess}</span>
                  </div>
                )}

                {/* Google Sign-In Button */}
                {googleEnabled && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full mb-2 bg-white hover:bg-gray-100 text-gray-900 border-gray-300 font-medium"
                      onClick={handleGoogleLogin}
                      disabled={googleLoading}
                      data-testid="google-login-btn"
                    >
                      {googleLoading ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <GoogleIcon />
                      )}
                      <span className="ml-2">Continue with Google</span>
                    </Button>
                    <p className="text-center text-gray-500 text-xs mb-4">
                      Sign in or create a new account instantly
                    </p>

                    <div className="relative mb-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-gray-700" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-[#111111] px-2 text-gray-500">Or continue with email</span>
                      </div>
                    </div>
                  </>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#1a1a1a]">
                    <TabsTrigger value="login" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Sign In</TabsTrigger>
                    <TabsTrigger value="register" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Register</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    {forgotPasswordMode ? renderForgotPasswordUI() : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="login-email" className="text-gray-300">Email</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="login-email"
                            type="email"
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="your@email.com"
                            autoComplete="email"
                            required
                            data-testid="login-email"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="login-password" className="text-gray-300">Password</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="login-password"
                            type="password"
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="••••••••"
                            autoComplete="current-password"
                            required
                            data-testid="login-password"
                          />
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        disabled={loading}
                        data-testid="login-submit"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          'Sign In'
                        )}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setForgotPasswordMode('email')}
                        className="w-full text-sm text-gray-400 hover:text-red-400 transition-colors mt-2"
                        data-testid="forgot-password-link"
                      >
                        Forgot your password?
                      </button>
                    </form>
                    )}
                  </TabsContent>

                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div>
                        <Label htmlFor="register-name" className="text-gray-300">Full Name</Label>
                        <div className="relative mt-1">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="register-name"
                            type="text"
                            value={registerData.full_name}
                            onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="John Doe"
                            required
                            data-testid="register-name"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="register-email" className="text-gray-300">Email</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="register-email"
                            type="email"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="your@email.com"
                            autoComplete="email"
                            required
                            data-testid="register-email"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="register-password" className="text-gray-300">Password</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="register-password"
                            type="password"
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="••••••••"
                            autoComplete="new-password"
                            required
                            data-testid="register-password"
                          />
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        disabled={loading}
                        data-testid="register-submit"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating account...
                          </>
                        ) : (
                          'Create Account'
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // If there's a promotional wallpaper, show it as full background
  if (promoWallpaper) {
    // Use mobile image if available and on mobile, otherwise use desktop image
    const backgroundImage = (isMobile && promoWallpaper.mobile_image_url) 
      ? promoWallpaper.mobile_image_url 
      : promoWallpaper.image_url;
    
    return (
      <div className="min-h-screen relative">
        {/* Full Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        {/* Dark overlay to ensure login box is readable */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Content Container - Centered */}
        <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="text-center mb-8">
              <div 
                className="inline-flex items-center gap-3 cursor-pointer"
                onClick={() => navigate(isVODDomain ? '/vod' : '/live')}
              >
                <img 
                  src={isVODDomain ? "/ltv-vod-logo.png" : "/ltv-live-logo.png"}
                  alt={isVODDomain ? "LTV VOD" : "LTV Live"}
                  className={isVODDomain ? "h-16 sm:h-20 object-contain" : "w-12 h-12 object-contain brightness-0 invert"}
                />
                {!isVODDomain && <span className="text-2xl font-bold text-white drop-shadow-lg">LTV Live</span>}
              </div>
            </div>

            <Card className="bg-[#111111]/95 border-[#222222] backdrop-blur-sm shadow-2xl">
              <CardHeader className="text-center">
                <CardTitle className="text-white text-2xl">Welcome</CardTitle>
                <CardDescription className="text-gray-400">{isVODDomain ? 'Sign in to access LTV VOD' : 'Sign in to access your live stream tickets'}</CardDescription>
              </CardHeader>
              <CardContent>
                {error && error === 'STAFF_PORTAL_REDIRECT' ? (
                  <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4 mb-4 text-amber-300">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm font-semibold">Staff Account Detected</span>
                    </div>
                    <p className="text-sm mb-3">This email is registered as a staff/admin account. Staff members must log in through the Admin Portal.</p>
                    <a href="/admin" className="inline-block bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                      Go to Admin Portal
                    </a>
                  </div>
                ) : error ? (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                ) : null}

                {forgotSuccess && (
                  <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-3 mb-4 flex items-center gap-2 text-green-400">
                    <span className="text-sm">{forgotSuccess}</span>
                  </div>
                )}

                {/* Google Sign-In Button */}
                {googleEnabled && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full mb-2 bg-white hover:bg-gray-100 text-gray-900 border-gray-300 font-medium"
                      onClick={handleGoogleLogin}
                      disabled={googleLoading}
                      data-testid="google-login-btn"
                    >
                      {googleLoading ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <GoogleIcon />
                      )}
                      <span className="ml-2">Continue with Google</span>
                    </Button>
                    <p className="text-center text-gray-500 text-xs mb-4">
                      Sign in or create a new account instantly
                    </p>

                    <div className="relative mb-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-gray-700" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-[#111111] px-2 text-gray-500">Or continue with email</span>
                      </div>
                    </div>
                  </>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#1a1a1a]">
                    <TabsTrigger value="login" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Sign In</TabsTrigger>
                    <TabsTrigger value="register" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Register</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    {forgotPasswordMode ? renderForgotPasswordUI() : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="login-email" className="text-gray-300">Email</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="login-email"
                            type="email"
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="your@email.com"
                            autoComplete="email"
                            required
                            data-testid="login-email"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="login-password" className="text-gray-300">Password</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="login-password"
                            type="password"
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="••••••••"
                            autoComplete="current-password"
                            required
                            data-testid="login-password"
                          />
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        disabled={loading}
                        data-testid="login-submit"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          'Sign In'
                        )}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setForgotPasswordMode('email')}
                        className="w-full text-sm text-gray-400 hover:text-red-400 transition-colors mt-2"
                        data-testid="forgot-password-link"
                      >
                        Forgot your password?
                      </button>
                    </form>
                    )}
                  </TabsContent>

                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div>
                        <Label htmlFor="register-name" className="text-gray-300">Full Name</Label>
                        <div className="relative mt-1">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="register-name"
                            type="text"
                            value={registerData.full_name}
                            onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="John Doe"
                            required
                            data-testid="register-name"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="register-email" className="text-gray-300">Email</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="register-email"
                            type="email"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="your@email.com"
                            autoComplete="email"
                            required
                            data-testid="register-email"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="register-password" className="text-gray-300">Password</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            id="register-password"
                            type="password"
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                            placeholder="••••••••"
                            autoComplete="new-password"
                            required
                            minLength={6}
                            data-testid="register-password"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        disabled={loading}
                        data-testid="register-submit"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating account...
                          </>
                        ) : (
                          'Create Account'
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>

                <div className="mt-6 text-center">
                  <Button 
                    variant="link" 
                    className="text-gray-400 hover:text-white"
                    onClick={() => navigate(isVODDomain ? '/vod' : '/live')}
                  >
                    ← {isVODDomain ? 'Back' : 'Back to Events'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <p className="text-center text-gray-400 text-xs mt-6 drop-shadow">
              By signing in, you agree to our Terms of Service and Privacy Policy.
              <br />
              Note: For security, only one device can be logged in at a time.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default layout (no promotional wallpaper)
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div 
              className="inline-flex items-center gap-3 cursor-pointer"
              onClick={() => navigate(isVODDomain ? '/vod' : '/live')}
            >
              <img 
                src={isVODDomain ? "/ltv-vod-logo.png" : "/ltv-live-logo.png"}
                alt={isVODDomain ? "LTV VOD" : "LTV Live"}
                className={isVODDomain ? "h-16 sm:h-20 object-contain" : "w-12 h-12 object-contain brightness-0 invert"}
              />
              {!isVODDomain && <span className="text-2xl font-bold text-white">LTV Live</span>}
            </div>
          </div>

        <Card className="bg-[#111111] border-[#222222]">
          <CardHeader className="text-center">
            <CardTitle className="text-white text-2xl">Welcome</CardTitle>
            <CardDescription className="text-gray-400">{isVODDomain ? 'Sign in to access LTV VOD' : 'Sign in to access your live stream tickets'}</CardDescription>
          </CardHeader>
          <CardContent>
            {error && error === 'STAFF_PORTAL_REDIRECT' ? (
              <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4 mb-4 text-amber-300">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-semibold">Staff Account Detected</span>
                </div>
                <p className="text-sm mb-3">This email is registered as a staff/admin account. Staff members must log in through the Admin Portal.</p>
                <a href="/admin" className="inline-block bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                  Go to Admin Portal
                </a>
              </div>
            ) : error ? (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            ) : null}

            {forgotSuccess && (
              <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-3 mb-4 flex items-center gap-2 text-green-400">
                <span className="text-sm">{forgotSuccess}</span>
              </div>
            )}

            {/* Google Sign-In Button */}
            {googleEnabled && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mb-2 bg-white hover:bg-gray-100 text-gray-900 border-gray-300 font-medium"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  data-testid="google-login-btn"
                >
                  {googleLoading ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <GoogleIcon />
                  )}
                  <span className="ml-2">Continue with Google</span>
                </Button>
                <p className="text-center text-gray-500 text-xs mb-4">
                  Sign in or create a new account instantly
                </p>

                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#111111] px-2 text-gray-500">Or continue with email</span>
                  </div>
                </div>
              </>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#1a1a1a]">
                <TabsTrigger value="login" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Sign In</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {forgotPasswordMode ? renderForgotPasswordUI() : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-gray-300">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        id="login-email"
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                        placeholder="your@email.com"
                        autoComplete="email"
                            required
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="login-password" className="text-gray-300">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        id="login-password"
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                        placeholder="••••••••"
                        autoComplete="current-password"
                            required
                        data-testid="login-password"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordMode('email')}
                    className="w-full text-sm text-gray-400 hover:text-red-400 transition-colors mt-2"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </form>
                )}
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="register-name" className="text-gray-300">Full Name</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        id="register-name"
                        type="text"
                        value={registerData.full_name}
                        onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                        className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                        placeholder="John Doe"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="register-email" className="text-gray-300">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        id="register-email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                        placeholder="your@email.com"
                        autoComplete="email"
                            required
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="register-password" className="text-gray-300">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        id="register-password"
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 bg-[#1a1a1a] border-[#333333] text-white"
                        placeholder="••••••••"
                        autoComplete="new-password"
                            required
                        minLength={6}
                        data-testid="register-password"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center">
              <Button 
                variant="link" 
                className="text-gray-400 hover:text-white"
                onClick={() => navigate(isVODDomain ? '/vod' : '/live')}
              >
                ← {isVODDomain ? 'Back' : 'Back to Events'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-gray-500 text-xs mt-6">
          By signing in, you agree to our Terms of Service and Privacy Policy.
          <br />
          Note: For security, only one device can be logged in at a time.
        </p>
        </div>
      </div>
      
      {/* Footer for VOD domain */}
      {isVODDomain && <VODFooter />}
    </div>
  );
};

export default LiveCustomerLogin;
